/**
 * Saved Items store.
 *
 * Works in two modes:
 *   1. Anonymous — saves to localStorage. Items are keyed by
 *      `${itemType}:${itemId}` so lookups are O(1).
 *   2. Authenticated — the server is the source of truth. The local
 *      store acts as an optimistic cache; mutations fire API calls
 *      and invalidate the React Query cache.
 *
 * On login, `mergeAnonymousToServer()` is called once to bulk-upload
 * any locally-saved items to the server, then the local store is
 * cleared.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SaveInput, SavedItemType } from './types'
import { saveItem, unsaveByItem, bulkSave } from './api'

interface LocalSave {
  itemType: SavedItemType
  itemId: string
  title: string
  url: string
  savedAt: string
}

interface SavedItemsState {
  /** Anonymous saves — keyed by `${type}:${id}`. */
  local: Record<string, LocalSave>
  /** Whether the anonymous items have been merged to the server. */
  merged: boolean

  isSaved: (type: SavedItemType, id: string) => boolean
  toggleLocal: (input: SaveInput) => void
  clearLocal: () => void
  setMerged: () => void
}

const key = (type: string, id: string) => `${type}:${id}`

export const useSavedItemsStore = create<SavedItemsState>()(
  persist(
    (set, get) => ({
      local: {},
      merged: false,

      isSaved: (type, id) => Boolean(get().local[key(type, id)]),

      toggleLocal: (input) =>
        set((state) => {
          const k = key(input.itemType, input.itemId)
          if (state.local[k]) {
            const { [k]: _, ...rest } = state.local
            return { local: rest }
          }
          return {
            local: {
              ...state.local,
              [k]: {
                itemType: input.itemType,
                itemId: input.itemId,
                title: input.title,
                url: input.url,
                savedAt: new Date().toISOString(),
              },
            },
          }
        }),

      clearLocal: () => set({ local: {} }),
      setMerged: () => set({ merged: true }),
    }),
    {
      name: 'abdallah-saved-items',
      partialize: (s) => ({ local: s.local, merged: s.merged }),
    },
  ),
)

/**
 * Merge anonymous saves to the server after login.
 * Call once from the auth store's `setSession` action.
 */
export async function mergeAnonymousToServer(): Promise<void> {
  const state = useSavedItemsStore.getState()
  if (state.merged) return
  const items = Object.values(state.local)
  if (items.length === 0) {
    state.setMerged()
    return
  }
  try {
    await bulkSave(items)
    state.clearLocal()
    state.setMerged()
  } catch {
    /* swallowed — the local items stay until the next attempt */
  }
}

/**
 * Toggle save for an authenticated user. Fires the API and returns
 * the new saved state. The caller should invalidate the React Query
 * cache for `['saved-items']` on success.
 */
export async function toggleServerSave(
  input: SaveInput,
  currentlySaved: boolean,
): Promise<boolean> {
  if (currentlySaved) {
    await unsaveByItem(input.itemType, input.itemId)
    return false
  }
  await saveItem(input)
  return true
}
